import { useMemo } from 'react';
import { useDomainUsage } from '@/api/manticore';
import { BarCard } from '../../../_shared/AnalyticsKit';
import { formatBytes } from '../../../_shared/analytics-format';

/** Top domains card — ranks this service's domains by volume. Sourced from the
 *  central usage store (already vendor-synced), not a live vendor call, so it's
 *  cheap. Ranks against the service's own domain ids (from the tab's domain
 *  options) rather than a family service_id match, so it works regardless of
 *  where the service sits in the family tree. Gated by `enabled` (a null range
 *  disables the query). */
export function TopDomainsCard({
  domainOptions,
  from,
  to,
}: {
  domainOptions: { value: string; label: string }[];
  from?: string;
  to?: string;
}) {
  const range = from && to ? { from, to } : null;
  const q = useDomainUsage(range);

  const items = useMemo(() => {
    const ids = new Set(domainOptions.map((d) => d.value));
    return (q.data ?? [])
      .filter((r) => ids.has(r.domain_id))
      .sort((a, b) => b.volume_bytes - a.volume_bytes)
      .slice(0, 15)
      .map((d) => ({
        label: d.domain_name,
        value: d.volume_bytes,
        display: formatBytes(d.volume_bytes),
      }));
  }, [q.data, domainOptions]);

  return (
    <BarCard
      title="Top domains"
      description="This service's domains ranked by edge volume over the window."
      items={items}
      loading={q.isLoading}
    />
  );
}
